'use client'

// Contact — Document 04. Multi-pathway contact page with the
// "Three Doors" pattern: visitor first picks an inquiry type, then
// gets a context-aware form that submits via mailto:. Below the
// form: direct contact lines and the institutional ecosystem grid.
// Both /contact and /contacto render this same component via
// cookie-driven next-intl locale.

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

// ─── Tokens ──────────────────────────────────────────────────────────────
const EARTH_DEEP   = '#1F1608'
const EARTH_FWD    = '#2E1F0A'
const CREAM        = '#F5ECD8'
const CREAM_80     = 'rgba(245,236,216,0.80)'
const CREAM_60     = 'rgba(245,236,216,0.60)'
const CREAM_45     = 'rgba(245,236,216,0.45)'
const CREAM_30     = 'rgba(245,236,216,0.30)'
const CREAM_18     = 'rgba(245,236,216,0.18)'
const CREAM_10     = 'rgba(245,236,216,0.10)'
const OLIVE        = '#5B7005'
const OLIVE_60     = 'rgba(91,112,5,0.60)'
const LIME         = '#9FAF08'
const ERROR        = '#C45A3A'   // muted red-brown
const FONT_BODY    = 'var(--font-body)'
const FONT_DISP    = 'var(--font-display)'
const FONT_SERIF   = 'var(--font-serif)'

const INBOX = 'biosoilterra@gmail.com'

// Institutional logos (re-using existing TECH assets — URL-encoded paths)
const LOGOS = [
  { src: '/TECH/FPdGi-Logo%202.png',        altKey: 'door3Tag1' },
  { src: '/TECH/FPdGi-Logo%204.png',        altKey: 'door3Tag1' },
  { src: '/TECH/FPdGi-Logo%201.jpg',        altKey: 'door3Tag1' },
  { src: '/TECH/FPdGi-Logo%203.jpg',        altKey: 'door3Tag1' },
  { src: '/TECH/Startupv-Logo.png',         altKey: 'door3Tag3' },
  { src: '/TECH/Pacto%20Verde%20Europeo.jpg', altKey: 'door3Tag2' },
] as const

type DoorId = 'collab' | 'team' | 'inst'
type FormState = {
  name: string; email: string;
  org: string; projectType: string; message: string; source: string;
  role: string; area: string; motivation: string; linkedin: string;
  institution: string; role3: string; inqType: string; description: string; nextStep: string;
}
const EMPTY: FormState = {
  name: '', email: '',
  org: '', projectType: '', message: '', source: '',
  role: '', area: '', motivation: '', linkedin: '',
  institution: '', role3: '', inqType: '', description: '', nextStep: '',
}

// ─── Door card ───────────────────────────────────────────────────────────
function DoorCard({
  doorId, number, label, title, body, tags, openLabel, selectedLabel,
  selected, anySelected, onSelect, animDelay,
}: {
  doorId: DoorId
  number: string
  label: string
  title: string
  body: string
  tags: string[]
  openLabel: string
  selectedLabel: string
  selected: boolean
  anySelected: boolean
  onSelect: (id: DoorId) => void
  animDelay: number
}) {
  const wrapRef = useRef<HTMLButtonElement>(null)
  const reduced = useReducedMotion()
  useEffect(() => {
    if (reduced || !wrapRef.current) return
    gsapSetup()
    const node = wrapRef.current
    gsap.set(node, { autoAlpha: 0, y: 28 })
    const trig = ScrollTrigger.create({
      trigger: node,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(node, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: animDelay })
      },
    })
    return () => trig.kill()
  }, [reduced, animDelay])

  const dim = anySelected && !selected
  return (
    <button
      ref={wrapRef}
      type="button"
      className={`door-card ${selected ? 'selected' : ''} ${dim ? 'dim' : ''}`}
      onClick={() => onSelect(doorId)}
      aria-pressed={selected}
      aria-label={title}
    >
      <span className="door-num" aria-hidden="true">{number}</span>
      <span className="door-icon" aria-hidden="true">
        {doorId === 'collab' && (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="9" cy="16" r="4" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="23" cy="16" r="4" stroke="currentColor" strokeWidth="1.4" />
            <path d="M13 16H19" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M3 24L9 20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M29 24L23 20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        )}
        {doorId === 'team' && (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="10" r="4" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="6"  cy="13" r="3" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="26" cy="13" r="3" stroke="currentColor" strokeWidth="1.4" />
            <path d="M9 24c0-3.3 3.1-6 7-6s7 2.7 7 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M3 26c0-2.5 1.6-4.5 4-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M29 26c0-2.5-1.6-4.5-4-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        )}
        {doorId === 'inst' && (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M4 28h24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M5 11L16 5l11 6" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M5 11h22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M9 13v13M15 13v13M17 13v13M23 13v13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        )}
      </span>
      <span className="door-label">{label}</span>
      <h3 className="door-title">{title}</h3>
      <p className="door-body">{body}</p>
      <ul className="door-tags">{tags.map((tg, i) => <li key={i} className="door-tag">{tg}</li>)}</ul>
      <span className="door-cta">{selected ? selectedLabel : openLabel}</span>
    </button>
  )
}

// ─── Field primitives ────────────────────────────────────────────────────
type FieldKey =
  | 'name' | 'email' | 'org' | 'projectType' | 'message' | 'source'
  | 'role' | 'area' | 'motivation' | 'linkedin'
  | 'institution' | 'role3' | 'inqType' | 'description' | 'nextStep'

type FieldDef = {
  key: FieldKey
  type: 'text' | 'email' | 'textarea' | 'select'
  labelKey: string
  required?: boolean
  optionKeys?: string[]
  placeholderKey?: string
  minChars?: number
}

const FIELDS: Record<DoorId, FieldDef[]> = {
  collab: [
    { key: 'name',        type: 'text',     labelKey: 'fName' },
    { key: 'email',       type: 'email',    labelKey: 'fEmail',  required: true },
    { key: 'org',         type: 'text',     labelKey: 'fOrg' },
    { key: 'projectType', type: 'select',   labelKey: 'fProjectType', optionKeys: ['p1opt1','p1opt2','p1opt3','p1opt4','p1opt5'] },
    { key: 'message',     type: 'textarea', labelKey: 'fMessage', required: true, minChars: 50 },
    { key: 'source',      type: 'text',     labelKey: 'fSource' },
  ],
  team: [
    { key: 'name',       type: 'text',     labelKey: 'fName' },
    { key: 'email',      type: 'email',    labelKey: 'fEmail',  required: true },
    { key: 'role',       type: 'text',     labelKey: 'fRole' },
    { key: 'area',       type: 'select',   labelKey: 'fArea', optionKeys: ['p2opt1','p2opt2','p2opt3','p2opt4','p2opt5','p2opt6'] },
    { key: 'motivation', type: 'textarea', labelKey: 'fMotivation', required: true, placeholderKey: 'fMotivationPh' },
    { key: 'linkedin',   type: 'text',     labelKey: 'fLinkedin' },
  ],
  inst: [
    { key: 'name',        type: 'text',     labelKey: 'fName' },
    { key: 'email',       type: 'email',    labelKey: 'fEmail',  required: true },
    { key: 'institution', type: 'text',     labelKey: 'fInst', required: true },
    { key: 'role3',       type: 'text',     labelKey: 'fRole3' },
    { key: 'inqType',     type: 'select',   labelKey: 'fInqType', optionKeys: ['p3opt1','p3opt2','p3opt3','p3opt4','p3opt5','p3opt6'] },
    { key: 'description', type: 'textarea', labelKey: 'fDescription', required: true },
    { key: 'nextStep',    type: 'select',   labelKey: 'fNextStep', optionKeys: ['nsopt1','nsopt2','nsopt3','nsopt4'] },
  ],
}

// ─── Page ────────────────────────────────────────────────────────────────
export type ContactPageLogo = { src: string; alt: string }

const FALLBACK_LOGOS: ContactPageLogo[] = LOGOS.map(l => ({ src: l.src, alt: '' }))

export default function ContactPageClient({ logos }: { logos?: ContactPageLogo[] } = {}) {
  const t = useTranslations('contactPage')
  const reduced = useReducedMotion()

  const [door, setDoor] = useState<DoorId | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
  const [submitted, setSubmitted] = useState(false)

  const formSectionRef = useRef<HTMLElement>(null)
  const formPanelRef   = useRef<HTMLDivElement>(null)
  const heroLine1Ref   = useRef<HTMLSpanElement>(null)
  const heroLine2Ref   = useRef<HTMLSpanElement>(null)
  const heroSubRef     = useRef<HTMLParagraphElement>(null)
  const heroPrevRef    = useRef<HTMLDivElement>(null)
  const heroCueRef     = useRef<HTMLDivElement>(null)

  // ── Hero entrance ──────────────────────────────────────────
  useEffect(() => {
    if (reduced) return
    gsapSetup()
    const tl = gsap.timeline({ delay: 0.15 })
    const stagger = (el: HTMLElement | null) => {
      if (!el) return
      const chars = el.querySelectorAll<HTMLElement>('[data-char]')
      gsap.set(chars, { autoAlpha: 0, y: 40 })
      tl.to(chars, { autoAlpha: 1, y: 0, duration: 0.75, ease: 'power3.out', stagger: 0.04 }, '<')
    }
    stagger(heroLine1Ref.current)
    if (heroLine2Ref.current) {
      const chars = heroLine2Ref.current.querySelectorAll<HTMLElement>('[data-char]')
      gsap.set(chars, { autoAlpha: 0, y: 40 })
      tl.to(chars, { autoAlpha: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.07 }, '-=0.55')
    }
    if (heroSubRef.current) {
      const words = heroSubRef.current.querySelectorAll<HTMLElement>('[data-word]')
      gsap.set(words, { autoAlpha: 0, y: 18 })
      tl.to(words, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.04 }, '-=0.4')
    }
    if (heroPrevRef.current) {
      const items = heroPrevRef.current.querySelectorAll<HTMLElement>('[data-prev]')
      gsap.set(items, { autoAlpha: 0, y: 12 })
      tl.to(items, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power3.out', stagger: 0.08 }, '-=0.3')
    }
    if (heroCueRef.current) {
      gsap.set(heroCueRef.current, { autoAlpha: 0 })
      tl.to(heroCueRef.current, { autoAlpha: 1, duration: 0.5 }, '-=0.2')
    }
  }, [reduced])

  // ── Door selection: animate + scroll ───────────────────────
  const handleSelectDoor = (id: DoorId) => {
    const switching = door !== null && door !== id
    setDoor(id)
    setSubmitted(false)
    setErrors({})

    // Cross-fade form panel when switching
    if (!reduced && formPanelRef.current && switching) {
      gsap.fromTo(formPanelRef.current,
        { autoAlpha: 0.2, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out' })
    }

    // Auto-scroll into the form section, slight delay so the
    // selection state is visible before the page moves.
    window.setTimeout(() => {
      const el = formSectionRef.current
      if (!el) return
      const y = el.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' })
    }, 600)
  }

  // ── Form helpers ───────────────────────────────────────────
  const update = (key: FieldKey, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }))
  }

  const validate = (): boolean => {
    if (!door) return false
    const fs = FIELDS[door]
    const next: Partial<Record<FieldKey, string>> = {}
    fs.forEach(f => {
      const v = form[f.key]
      if (f.required && !v.trim()) next[f.key] = t('vRequired')
      else if (f.type === 'email' && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) next[f.key] = t('vEmail')
      else if (f.minChars && v && v.trim().length < f.minChars) next[f.key] = t('vMin')
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // ── Build mailto for the selected door ─────────────────────
  const buildMailto = (): string | null => {
    if (!door) return null
    const lines: string[] = []
    let subject = ''
    if (door === 'collab') {
      subject = `${t('mailSubject1')} ${form.name || '—'} · ${form.org || '—'}`
      lines.push(`${t('mailLabelOrg')}: ${form.org}`)
      lines.push(`${t('mailLabelProject')}: ${form.projectType}`)
      lines.push('')
      lines.push(`${t('mailLabelMessage')}:`)
      lines.push(form.message)
      if (form.source) { lines.push(''); lines.push(`${t('mailLabelSource')}: ${form.source}`) }
    } else if (door === 'team') {
      subject = `${t('mailSubject2')} ${form.name || '—'} · ${form.area || '—'}`
      lines.push(`${t('mailLabelRole')}: ${form.role}`)
      lines.push(`${t('mailLabelArea')}: ${form.area}`)
      lines.push('')
      lines.push(`${t('mailLabelMessage')}:`)
      lines.push(form.motivation)
      if (form.linkedin) { lines.push(''); lines.push(`${t('mailLabelLinkedin')}: ${form.linkedin}`) }
    } else {
      subject = `${t('mailSubject3')} ${form.institution || '—'} · ${form.name || '—'}`
      lines.push(`${t('mailLabelOrg')}: ${form.institution}`)
      lines.push(`${t('mailLabelRole')}: ${form.role3}`)
      lines.push(`${t('mailLabelType')}: ${form.inqType}`)
      lines.push('')
      lines.push(`${t('mailLabelMessage')}:`)
      lines.push(form.description)
      lines.push('')
      lines.push(`${t('mailLabelNextStep')}: ${form.nextStep}`)
    }
    return `mailto:${INBOX}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    const url = buildMailto()
    if (!url) return
    window.location.href = url
    setSubmitted(true)
  }

  const handleReset = () => {
    setSubmitted(false)
    setForm(EMPTY)
    setErrors({})
  }

  // ── Form panel content ─────────────────────────────────────
  const formMeta = useMemo(() => {
    if (!door) return null
    if (door === 'collab') return { label: t('form1Label'), head: t('form1Headline'), body: t('form1Context'), btn: t('form1Btn') }
    if (door === 'team')   return { label: t('form2Label'), head: t('form2Headline'), body: t('form2Context'), btn: t('form2Btn') }
    return                       { label: t('form3Label'), head: t('form3Headline'), body: t('form3Context'), btn: t('form3Btn') }
  }, [door, t])

  // ── Render fields ──────────────────────────────────────────
  const renderField = (f: FieldDef) => {
    const id = `f-${f.key}`
    const lbl = t(f.labelKey)
    const err = errors[f.key]
    const placeholder = f.placeholderKey ? t(f.placeholderKey) : undefined
    const v = form[f.key]
    return (
      <div key={f.key} className={`field ${err ? 'has-error' : ''}`}>
        <label htmlFor={id} className="field-label">
          {lbl}{f.required && <span aria-hidden="true" className="field-req">*</span>}
        </label>
        {f.type === 'textarea' ? (
          <textarea
            id={id}
            value={v}
            onChange={e => update(f.key, e.target.value)}
            placeholder={placeholder}
            rows={4}
            aria-invalid={!!err}
            aria-describedby={err ? `${id}-err` : undefined}
          />
        ) : f.type === 'select' ? (
          <select
            id={id}
            value={v}
            onChange={e => update(f.key, e.target.value)}
            aria-invalid={!!err}
            aria-describedby={err ? `${id}-err` : undefined}
          >
            <option value="">{t('selectPlaceholder')}</option>
            {f.optionKeys?.map(ok => (
              <option key={ok} value={t(ok)}>{t(ok)}</option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            type={f.type}
            value={v}
            onChange={e => update(f.key, e.target.value)}
            placeholder={placeholder}
            aria-invalid={!!err}
            aria-describedby={err ? `${id}-err` : undefined}
          />
        )}
        <span className="field-rule" aria-hidden="true" />
        {err && <span id={`${id}-err`} className="field-err">{err}</span>}
      </div>
    )
  }

  return (
    <main
      style={{
        background: EARTH_DEEP,
        color: CREAM,
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .ct-content { max-width: 1280px; margin: 0 auto; padding: 0 clamp(24px, 5vw, 80px); position: relative; }
        .ct-grain {
          position: absolute; inset: 0;
          background-image: url(/grain.svg); background-size: 220px 220px;
          opacity: 0.06; mix-blend-mode: soft-light; pointer-events: none; z-index: 1;
        }
        .ct-chrome {
          display: flex; justify-content: space-between; align-items: center;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM_45};
        }

        /* ── Hero ───────────────────────────────────────────── */
        .ct-hero-wrap {
          display: flex; flex-direction: column; align-items: center;
          text-align: center;
          flex: 1; justify-content: center;
          padding-top: clamp(80px, 12vh, 140px);
        }
        .ct-hero-eyebrow {
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 600; color: ${OLIVE};
          margin: 0 0 clamp(24px, 4vh, 40px);
        }
        .ct-hero-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(5rem, 12vw, 10rem);
          line-height: 0.95; letter-spacing: -0.045em;
          color: ${CREAM}; margin: 0;
        }
        .ct-hero-h-l2 {
          display: block;
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          color: ${LIME};
          letter-spacing: -0.03em;
        }
        .ct-hero-h [data-char] {
          display: inline-block;
          will-change: transform, opacity;
        }
        .ct-hero-sub {
          font-family: ${FONT_BODY}; font-size: clamp(15px, 1.2vw, 18px);
          line-height: 1.65; color: ${CREAM_80};
          margin: clamp(28px, 4vh, 40px) auto 0; max-width: 600px;
        }
        .ct-hero-sub [data-word] { display: inline-block; margin-right: 0.25em; }
        .ct-hero-prev {
          display: flex; flex-wrap: wrap; gap: 6px 18px; justify-content: center;
          margin-top: clamp(24px, 3.5vh, 36px);
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          font-weight: 600; color: ${OLIVE};
        }
        .ct-hero-prev > span { display: inline-block; }
        .ct-hero-prev > i {
          font-style: normal; color: ${CREAM_30}; padding: 0 4px;
        }
        .ct-hero-cue {
          margin-top: clamp(48px, 7vh, 72px);
          display: flex; flex-direction: column; align-items: center; gap: 10px;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          color: ${CREAM_60}; font-weight: 600;
        }
        .ct-hero-cue-line {
          display: block; width: 1px; height: 36px;
          background: linear-gradient(to bottom, ${CREAM_45}, transparent);
          animation: ct-cue-pulse 2.4s ease-in-out infinite;
        }
        @keyframes ct-cue-pulse {
          0%, 100% { opacity: 0.5; transform: scaleY(1); }
          50%      { opacity: 1;   transform: scaleY(1.15); }
        }
        .ct-hero-bottom {
          display: flex; justify-content: flex-end;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          color: ${CREAM_45}; font-weight: 600;
        }

        /* ── Section header ─────────────────────────────────── */
        .ct-shead-label {
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 700; color: ${LIME};
          margin: 0 0 18px;
        }
        .ct-shead-title {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(2.5rem, 5vw, 4rem);
          line-height: 1.0; letter-spacing: -0.025em;
          color: ${CREAM}; margin: 0;
        }
        .ct-shead-sub {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.25rem, 2.5vw, 1.75rem);
          color: ${LIME}; margin: 14px 0 0; line-height: 1.3;
          letter-spacing: -0.01em;
        }

        /* ── Doors ──────────────────────────────────────────── */
        .ct-doors {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(18px, 2vw, 26px);
          margin-top: clamp(56px, 8vh, 96px);
        }
        @media (max-width: 1023px) { .ct-doors { grid-template-columns: 1fr; } }

        .door-card {
          position: relative;
          display: flex; flex-direction: column;
          gap: 18px;
          height: clamp(440px, 56vh, 520px);
          padding: clamp(24px, 2.4vw, 32px);
          background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%);
          border: 1px solid ${CREAM_18};
          border-radius: 14px;
          color: ${CREAM};
          font-family: inherit;
          text-align: left;
          cursor: pointer;
          transition:
            transform 380ms cubic-bezier(0.22,1,0.36,1),
            border-color 380ms ease,
            background 380ms ease,
            box-shadow 380ms ease,
            opacity 380ms ease,
            filter 380ms ease;
          overflow: hidden;
        }
        .door-card::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse 70% 40% at 50% 0%, rgba(159,175,8,0.15), transparent 70%);
          opacity: 0; transition: opacity 380ms ease;
          pointer-events: none;
        }
        .door-card:hover {
          transform: translateY(-8px);
          border-color: ${OLIVE_60};
          background: linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%);
          box-shadow: 0 20px 50px rgba(0,0,0,0.45);
        }
        .door-card:hover::before { opacity: 0.6; }
        .door-card:focus-visible {
          outline: 2px solid ${LIME}; outline-offset: 4px;
        }
        .door-card.dim { opacity: 0.5; filter: saturate(0.6); }
        .door-card.selected {
          border-color: ${LIME};
          box-shadow:
            0 24px 60px rgba(0,0,0,0.55),
            0 0 0 1px ${LIME},
            0 0 36px rgba(159,175,8,0.18);
          animation: door-pulse 3.2s ease-in-out infinite;
        }
        .door-card.selected::before { opacity: 1; }
        @keyframes door-pulse {
          0%, 100% { box-shadow: 0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px ${LIME}, 0 0 36px rgba(159,175,8,0.18); }
          50%      { box-shadow: 0 26px 72px rgba(0,0,0,0.55), 0 0 0 1px ${LIME}, 0 0 56px rgba(159,175,8,0.30); }
        }

        .door-num {
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          font-size: clamp(5rem, 7vw, 6.5rem);
          line-height: 0.85; letter-spacing: -0.04em;
          color: ${OLIVE};
          opacity: 0.7;
          margin-bottom: -8px;
          transition: color 380ms ease, transform 380ms ease, opacity 380ms ease;
        }
        .door-card:hover .door-num,
        .door-card.selected .door-num { color: ${LIME}; opacity: 1; transform: scale(1.04); }

        .door-icon { color: ${OLIVE}; opacity: 0.8; transition: color 320ms ease; }
        .door-card:hover .door-icon,
        .door-card.selected .door-icon { color: ${LIME}; opacity: 1; }

        .door-label {
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.36em; text-transform: uppercase;
          font-weight: 600; color: ${OLIVE};
        }
        .door-title {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.4rem, 1.8vw, 1.75rem);
          color: ${CREAM}; margin: 0;
          letter-spacing: -0.02em; line-height: 1.15;
        }
        .door-body {
          font-family: ${FONT_BODY}; font-size: 13.5px;
          line-height: 1.55; color: ${CREAM_60};
          margin: 0;
        }
        .door-tags {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-wrap: wrap; gap: 6px;
        }
        .door-tag {
          padding: 5px 10px;
          border: 1px solid ${CREAM_18};
          border-radius: 999px;
          font-family: ${FONT_BODY}; font-size: 9.5px;
          letter-spacing: 0.28em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM_60};
        }
        .door-card.selected .door-tag { border-color: ${OLIVE_60}; color: ${CREAM_80}; }
        .door-cta {
          margin-top: auto;
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.32em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM_80};
        }
        .door-card.selected .door-cta { color: ${LIME}; }

        /* ── Form section ───────────────────────────────────── */
        .ct-form-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.7fr) minmax(0, 1fr);
          gap: clamp(40px, 5vw, 80px);
          margin-top: clamp(40px, 6vh, 64px);
          align-items: flex-start;
        }
        @media (max-width: 1023px) { .ct-form-grid { grid-template-columns: 1fr; gap: 32px; } }

        .ct-form-side-label {
          display: inline-flex; align-items: center; gap: 10px;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          font-weight: 600; color: ${LIME};
          margin: 0 0 22px;
        }
        .ct-form-side-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: ${LIME}; box-shadow: 0 0 8px ${LIME};
          animation: ct-form-dot 1.6s ease-in-out infinite;
        }
        @keyframes ct-form-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.4); opacity: 0.5; }
        }
        .ct-form-side-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          color: ${CREAM}; margin: 0 0 22px;
          letter-spacing: -0.02em; line-height: 1.1;
        }
        .ct-form-side-body {
          font-family: ${FONT_BODY}; font-size: 14px;
          line-height: 1.7; color: ${CREAM_60};
          margin: 0; max-width: 38ch;
        }

        .ct-form-default {
          padding: clamp(40px, 6vh, 80px) 0;
          text-align: center;
        }
        .ct-form-default-arrow {
          display: inline-block;
          font-size: 28px; color: ${LIME};
          animation: ct-default-bounce 1.6s ease-in-out infinite;
          margin-bottom: 20px;
        }
        @keyframes ct-default-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        .ct-form-default-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.5rem, 2.4vw, 2rem);
          color: ${CREAM}; margin: 0 0 12px;
          letter-spacing: -0.02em;
        }
        .ct-form-default-body {
          font-family: ${FONT_BODY}; font-size: 14px;
          color: ${CREAM_60}; margin: 0;
        }

        /* ── Form fields (editorial: underline-only) ────────── */
        .ct-form { display: flex; flex-direction: column; gap: 32px; }
        .field { position: relative; display: flex; flex-direction: column; gap: 10px; }
        .field-label {
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.32em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM_60};
        }
        .field-req { color: ${LIME}; margin-left: 4px; }
        .field input,
        .field textarea,
        .field select {
          width: 100%;
          background: transparent;
          color: ${CREAM};
          font-family: ${FONT_BODY};
          font-size: 17px;
          line-height: 1.5;
          padding: 6px 0;
          border: 0;
          border-bottom: 1px solid ${CREAM_30};
          outline: none;
          appearance: none;
          -webkit-appearance: none;
          font-weight: 400;
        }
        .field textarea { resize: vertical; min-height: 96px; padding-bottom: 10px; }
        .field input::placeholder,
        .field textarea::placeholder { color: rgba(245,236,216,0.40); }
        .field select { color: ${CREAM_80}; cursor: pointer; padding-right: 24px;
          background-image: linear-gradient(45deg, transparent 50%, ${CREAM_60} 50%),
                            linear-gradient(135deg, ${CREAM_60} 50%, transparent 50%);
          background-position: right 8px top 16px, right 4px top 16px;
          background-size: 4px 4px, 4px 4px;
          background-repeat: no-repeat;
        }
        .field select option { background: ${EARTH_DEEP}; color: ${CREAM}; }
        .field-rule {
          position: absolute; left: 0; bottom: 0; height: 1px; width: 0;
          background: ${LIME};
          box-shadow: 0 0 8px rgba(159,175,8,0.55);
          transition: width 380ms cubic-bezier(0.22,1,0.36,1);
        }
        .field:focus-within .field-rule { width: 100%; }
        .field.has-error input,
        .field.has-error textarea,
        .field.has-error select { border-bottom-color: ${ERROR}; }
        .field-err {
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.28em; text-transform: uppercase;
          font-weight: 600; color: ${ERROR};
        }

        .ct-form-submit {
          align-self: flex-start;
          background: transparent; border: 0; cursor: pointer;
          padding: 12px 0;
          font-family: ${FONT_BODY}; font-size: 16px;
          letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${LIME};
          position: relative;
          transition: color 280ms ease, transform 280ms ease;
        }
        .ct-form-submit::after {
          content: ''; position: absolute; left: 0; bottom: 6px; height: 1px; width: 0;
          background: ${LIME};
          transition: width 380ms cubic-bezier(0.22,1,0.36,1);
        }
        .ct-form-submit:hover { transform: translateX(2px); }
        .ct-form-submit:hover::after { width: 100%; }
        .ct-form-submit:active { transform: scale(0.98); }

        .ct-success {
          padding: clamp(40px, 6vh, 80px) 0;
        }
        .ct-success-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(2rem, 3.6vw, 3rem);
          color: ${CREAM}; margin: 0 0 14px;
          letter-spacing: -0.025em; line-height: 1.1;
        }
        .ct-success-sub {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.1rem, 1.6vw, 1.45rem);
          color: ${LIME}; margin: 0 0 32px; font-weight: 400;
        }
        .ct-success-reset {
          background: transparent; border: 0; cursor: pointer;
          font-family: ${FONT_BODY}; font-size: 13px;
          letter-spacing: 0.22em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM_80};
          transition: color 240ms ease;
        }
        .ct-success-reset:hover { color: ${LIME}; }

        /* ── Direct contact ─────────────────────────────────── */
        .ct-direct-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: clamp(40px, 6vw, 80px);
          margin-top: clamp(56px, 8vh, 96px);
        }
        @media (max-width: 1023px) { .ct-direct-grid { grid-template-columns: 1fr; gap: 48px; } }

        .ct-block { padding: 0 0 clamp(28px, 4vh, 40px); }
        .ct-block + .ct-block { border-top: 1px solid ${CREAM_10}; padding-top: clamp(28px, 4vh, 40px); }
        .ct-block-label {
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          font-weight: 600; color: ${OLIVE};
          margin: 0 0 12px;
        }
        .ct-block-val {
          font-family: ${FONT_BODY}; font-size: clamp(18px, 1.5vw, 22px);
          font-weight: 500; color: ${CREAM};
          line-height: 1.3;
        }
        .ct-block-sub {
          font-family: ${FONT_BODY}; font-size: 13px;
          color: ${CREAM_60}; margin-top: 6px;
        }
        .ct-block a {
          color: ${CREAM};
          text-decoration: none;
          background-image: linear-gradient(${LIME}, ${LIME});
          background-repeat: no-repeat;
          background-size: 0% 1px;
          background-position: 0 100%;
          transition: background-size 380ms cubic-bezier(0.22,1,0.36,1), color 240ms ease;
        }
        .ct-block a:hover { background-size: 100% 1px; color: ${LIME}; }
        .ct-block-social a + a { margin-left: 18px; }

        .ct-inst-h {
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 700; color: ${LIME};
          margin: 0 0 14px;
        }
        .ct-inst-sub {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.1rem, 1.6vw, 1.4rem);
          color: ${CREAM_80}; margin: 0 0 32px; line-height: 1.4;
          font-weight: 400;
        }
        .ct-inst-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        @media (max-width: 1023px) { .ct-inst-grid { grid-template-columns: repeat(2, 1fr); } }
        .ct-inst-tile {
          height: 96px;
          display: flex; align-items: center; justify-content: center;
          padding: 14px 18px;
          border: 1px solid ${CREAM_10};
          border-radius: 10px;
          background: rgba(255,255,255,0.025);
          transition: transform 320ms cubic-bezier(0.22,1,0.36,1),
                      border-color 320ms ease, background 320ms ease,
                      box-shadow 320ms ease;
          position: relative;
        }
        .ct-inst-tile img {
          filter: grayscale(100%) brightness(0.7);
          opacity: 0.78;
          transition: filter 320ms ease, opacity 320ms ease, transform 320ms ease;
        }
        .ct-inst-tile:hover {
          transform: translateY(-3px);
          border-color: ${OLIVE_60};
          background: rgba(255,255,255,0.06);
          box-shadow: 0 14px 32px rgba(0,0,0,0.4);
        }
        .ct-inst-tile:hover img {
          filter: none; opacity: 1; transform: scale(1.04);
        }
        .ct-inst-placeholder {
          height: 96px; display: flex; align-items: center; justify-content: center;
          border: 1px dashed ${CREAM_18}; border-radius: 10px;
          font-family: ${FONT_BODY}; font-size: 9.5px;
          letter-spacing: 0.32em; text-transform: uppercase;
          color: ${CREAM_45}; font-weight: 600;
          padding: 10px; text-align: center;
        }
        .ct-inst-footer {
          font-family: ${FONT_BODY}; font-size: 12px;
          color: ${CREAM_45}; line-height: 1.6;
          margin: 24px 0 0; max-width: 60ch;
        }

        /* ── Footer whisper ─────────────────────────────────── */
        .ct-footer {
          padding: clamp(64px, 10vh, 120px) 0 clamp(40px, 6vh, 64px);
          border-top: 1px solid ${CREAM_10};
          margin-top: clamp(64px, 10vh, 120px);
          text-align: center;
        }
        .ct-footer-l1 {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.5rem, 2.6vw, 2.2rem);
          color: ${CREAM}; margin: 0;
          letter-spacing: -0.02em; line-height: 1.2;
        }
        .ct-footer-l2 {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.1rem, 1.6vw, 1.4rem);
          color: ${LIME}; margin: 12px 0 0; font-weight: 400;
        }
        .ct-footer-cr {
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.36em; text-transform: uppercase;
          color: ${CREAM_45}; font-weight: 600;
          margin-top: clamp(40px, 6vh, 64px);
        }

        /* ── Reduced motion ─────────────────────────────────── */
        @media (prefers-reduced-motion: reduce) {
          .ct-hero-cue-line,
          .ct-form-side-dot,
          .ct-form-default-arrow,
          .door-card.selected { animation: none !important; }
          .door-card { transition: opacity 200ms ease, border-color 200ms ease !important; }
          .door-card:hover { transform: none !important; }
        }
      `}</style>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section
        id="contact-hero"
        style={{
          position: 'relative',
          minHeight: '100vh',
          background: EARTH_DEEP,
          paddingTop:    'clamp(80px, 11vh, 140px)',
          paddingBottom: 'clamp(40px, 6vh, 80px)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="ct-grain" />

        <div className="ct-content ct-hero-wrap" style={{ position: 'relative', zIndex: 4 }}>
          <p className="ct-hero-eyebrow">{t('heroEyebrow')}</p>
          <h1 className="ct-hero-h">
            <span ref={heroLine1Ref} style={{ display: 'block' }}>
              {Array.from(t('heroLine1')).map((c, i) => (
                <span key={i} data-char>{c === ' ' ? ' ' : c}</span>
              ))}
            </span>
            <span ref={heroLine2Ref} className="ct-hero-h-l2">
              {Array.from(t('heroLine2')).map((c, i) => (
                <span key={i} data-char>{c === ' ' ? ' ' : c}</span>
              ))}
            </span>
          </h1>
          <p ref={heroSubRef} className="ct-hero-sub">
            {t('heroSubhead').split(' ').map((w, i) => (
              <span key={i} data-word>{w}</span>
            ))}
          </p>
          <div ref={heroPrevRef} className="ct-hero-prev" aria-hidden="true">
            <span data-prev>{t('heroPreview1')}</span><i>·</i>
            <span data-prev>{t('heroPreview2')}</span><i>·</i>
            <span data-prev>{t('heroPreview3')}</span>
          </div>
          <div ref={heroCueRef} className="ct-hero-cue">
            <span>{t('scrollCue')}</span>
            <span aria-hidden="true" className="ct-hero-cue-line" />
          </div>
        </div>

        <div className="ct-content ct-hero-bottom" style={{ position: 'relative', zIndex: 4, marginTop: 24 }}>
          <span>{t('pageIndicator')}</span>
        </div>
      </section>

      {/* ── 1 · THREE DOORS ─────────────────────────────────────── */}
      <section
        id="contact-doors"
        style={{
          position: 'relative',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${EARTH_DEEP} 0%, ${EARTH_FWD} 100%)`,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(96px, 14vh, 160px)',
          overflow: 'hidden',
        }}
      >
        <div className="ct-grain" />
        <div className="ct-content">
          <div className="ct-shead-label">{t('doorsLabel')}</div>
          <h2 className="ct-shead-title">{t('doorsTitle')}</h2>
          <p className="ct-shead-sub">{t('doorsSubtitle')}</p>

          <div className="ct-doors" role="radiogroup" aria-label={t('doorsTitle')}>
            <DoorCard
              doorId="collab" number={t('door1Number')} label={t('door1Label')}
              title={t('door1Title')} body={t('door1Body')}
              tags={[t('door1Tag1'), t('door1Tag2'), t('door1Tag3'), t('door1Tag4')]}
              openLabel={t('doorOpen')} selectedLabel={t('doorSelected')}
              selected={door === 'collab'} anySelected={door !== null}
              onSelect={handleSelectDoor} animDelay={0}
            />
            <DoorCard
              doorId="team" number={t('door2Number')} label={t('door2Label')}
              title={t('door2Title')} body={t('door2Body')}
              tags={[t('door2Tag1'), t('door2Tag2'), t('door2Tag3'), t('door2Tag4')]}
              openLabel={t('doorOpen')} selectedLabel={t('doorSelected')}
              selected={door === 'team'} anySelected={door !== null}
              onSelect={handleSelectDoor} animDelay={0.18}
            />
            <DoorCard
              doorId="inst" number={t('door3Number')} label={t('door3Label')}
              title={t('door3Title')} body={t('door3Body')}
              tags={[t('door3Tag1'), t('door3Tag2'), t('door3Tag3'), t('door3Tag4')]}
              openLabel={t('doorOpen')} selectedLabel={t('doorSelected')}
              selected={door === 'inst'} anySelected={door !== null}
              onSelect={handleSelectDoor} animDelay={0.36}
            />
          </div>
        </div>
      </section>

      {/* ── 2 · FORM ────────────────────────────────────────────── */}
      <section
        id="contact-form"
        ref={formSectionRef}
        style={{
          position: 'relative',
          background: EARTH_DEEP,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(96px, 14vh, 160px)',
          overflow: 'hidden',
        }}
      >
        <div className="ct-grain" />
        <div className="ct-content" ref={formPanelRef}>
          {!door ? (
            <div className="ct-form-default" role="status" aria-live="polite">
              <span className="ct-form-default-arrow" aria-hidden="true">↑</span>
              <h2 className="ct-form-default-h">{t('formDefaultHead')}</h2>
              <p className="ct-form-default-body">{t('formDefaultBody')}</p>
            </div>
          ) : submitted ? (
            <div className="ct-success" role="status" aria-live="polite">
              <h2 className="ct-success-h">{t('successHead')}</h2>
              <p className="ct-success-sub">{t('successSub')}</p>
              <button type="button" className="ct-success-reset" onClick={handleReset}>{t('successReset')}</button>
            </div>
          ) : (
            <div className="ct-form-grid">
              <aside>
                <div className="ct-form-side-label">
                  <span aria-hidden="true" className="ct-form-side-dot" />
                  <span>{formMeta!.label}</span>
                </div>
                <h2 className="ct-form-side-h">{formMeta!.head}</h2>
                <p className="ct-form-side-body">{formMeta!.body}</p>
              </aside>

              <form className="ct-form" onSubmit={handleSubmit} noValidate>
                {FIELDS[door].map(renderField)}
                <button type="submit" className="ct-form-submit">{formMeta!.btn}</button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ── 3 · DIRECT LINES + INSTITUTIONAL ─────────────────────── */}
      <section
        id="contact-direct"
        style={{
          position: 'relative',
          background: EARTH_DEEP,
          paddingTop:    'clamp(72px, 10vh, 120px)',
          paddingBottom: 'clamp(72px, 10vh, 120px)',
          overflow: 'hidden',
        }}
      >
        <div className="ct-grain" />
        <div className="ct-content">
          <div className="ct-shead-label">{t('directLabel')}</div>
          <h2 className="ct-shead-title">{t('directTitle')}</h2>
          <p className="ct-shead-sub">{t('directSubtitle')}</p>

          <div className="ct-direct-grid">
            {/* Left — direct contact blocks */}
            <div>
              <div className="ct-block">
                <div className="ct-block-label">{t('blockEmail')}</div>
                <div className="ct-block-val"><a href={`mailto:${INBOX}`}>{INBOX}</a></div>
              </div>
              <div className="ct-block">
                <div className="ct-block-label">{t('blockPhone')}</div>
                <div className="ct-block-val"><a href={`tel:${t('phoneValue').replace(/\s/g,'')}`}>{t('phoneValue')}</a></div>
              </div>
              <div className="ct-block">
                <div className="ct-block-label">{t('blockLocation')}</div>
                <div className="ct-block-val">{t('locationValue')}</div>
                <div className="ct-block-sub">{t('locationSub')}</div>
              </div>
              <div className="ct-block">
                <div className="ct-block-label">{t('blockResponse')}</div>
                <div className="ct-block-val">{t('responseValue')}</div>
              </div>
              <div className="ct-block ct-block-social">
                <div className="ct-block-label">{t('blockSocial')}</div>
                <div className="ct-block-val">
                  <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer">{t('linkLinkedin')} ↗</a>
                  <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer">{t('linkInstagram')} ↗</a>
                </div>
              </div>
            </div>

            {/* Right — institutional ecosystem */}
            <div>
              <div className="ct-inst-h">{t('instHeader')}</div>
              <p className="ct-inst-sub">{t('instSub')}</p>
              <div className="ct-inst-grid">
                {(logos && logos.length > 0 ? logos : FALLBACK_LOGOS).map((l, i) => (
                  <div key={i} className="ct-inst-tile" title={l.alt || t('door3Tag1')}>
                    <Image
                      src={l.src}
                      alt={l.alt || t('door3Tag1')}
                      width={140}
                      height={70}
                      unoptimized={l.src.startsWith('http')}
                      style={{ objectFit: 'contain', maxHeight: 60, width: 'auto' }}
                    />
                  </div>
                ))}
                <div className="ct-inst-placeholder">{t('logoPlaceholder')}</div>
                <div className="ct-inst-placeholder">{t('logoPlaceholder')}</div>
              </div>
              <p className="ct-inst-footer">{t('instFooter')}</p>
            </div>
          </div>

          {/* ── 4 · FOOTER WHISPER ───────────────────────────────── */}
          <div className="ct-footer">
            <p className="ct-footer-l1">{t('footerLine1')}</p>
            <p className="ct-footer-l2">{t('footerLine2')}</p>
            <p className="ct-footer-cr">{t('footerCopyright')}</p>
          </div>
        </div>
      </section>
    </main>
  )
}
